import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../SEO/SEOHead';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const stagger = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

/* ─────────────────────────────────────────────────────────────────
   MOCKUP COMPONENTS — inline SVG-based UI mockups for each solution
   ───────────────────────────────────────────────────────────────── */

const NMEMockup = () => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-700/50 bg-[#0f1729]">
    {/* Title bar */}
    <div className="flex items-center gap-2 px-4 py-3 bg-[#0d1321] border-b border-gray-700/50">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/80" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <div className="w-3 h-3 rounded-full bg-green-500/80" />
      </div>
      <span className="text-xs text-gray-500 ml-2 font-mono">North Marketing Engine v0.1.0</span>
    </div>
    <div className="flex">
      {/* Sidebar */}
      <div className="w-44 bg-[#0d1321] border-r border-gray-700/30 p-4 hidden md:block">
        <div className="text-white font-bold text-sm mb-1">North</div>
        <div className="text-gray-500 text-xs mb-6">Marketing Engine</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 rounded-lg text-blue-400 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
            Overview
          </div>
          <div className="flex items-center gap-2 px-3 py-2 text-gray-400 text-xs">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            Signals
          </div>
          <div className="flex items-center gap-2 px-3 py-2 text-gray-400 text-xs">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Content
          </div>
          <div className="flex items-center gap-2 px-3 py-2 text-gray-400 text-xs">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            Review
            <span className="ml-auto bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">26</span>
          </div>
        </div>
      </div>
      {/* Main content */}
      <div className="flex-1 p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h4 className="text-white font-bold text-base">Dashboard</h4>
            <p className="text-gray-500 text-xs">North Marketing Engine overview</p>
          </div>
          <div className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg flex items-center gap-1.5">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Run Pipeline
          </div>
        </div>
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Signal Reports', value: '11', sub: 'total aggregations', color: 'text-white' },
            { label: 'Total Content', value: '26', sub: 'pieces generated', color: 'text-white' },
            { label: 'Drafts Pending', value: '26', sub: 'review queue', color: 'text-orange-400' },
            { label: 'Approved', value: '0', sub: '0 published', color: 'text-green-400' },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#141d32] rounded-xl p-3 border border-gray-700/30">
              <div className={`text-[10px] uppercase tracking-wider ${stat.color === 'text-white' ? 'text-gray-500' : stat.color} font-medium mb-1`}>{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-gray-600 text-[10px]">{stat.sub}</div>
            </div>
          ))}
        </div>
        {/* Signal report preview */}
        <div className="bg-[#141d32] rounded-xl p-4 border border-gray-700/30">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            <span className="text-white font-semibold text-sm">Latest Signal Report</span>
          </div>
          <p className="text-gray-500 text-xs mb-3">Report #11 — 65 signals analyzed</p>
          <div className="space-y-2">
            {[
              { topic: 'AI Agent Cost & Efficiency', tag: 'ai-implementation', score: '8.0' },
              { topic: 'Multi-Agent Orchestration', tag: 'ai-implementation', score: '7.0' },
              { topic: 'Cloud Native AI Security', tag: 'cloud-native', score: '6.0' },
            ].map((cluster) => (
              <div key={cluster.topic} className="bg-[#0d1321] rounded-lg p-3 border border-gray-700/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white text-xs font-medium">{cluster.topic}</span>
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-600/20 text-blue-400 rounded">{cluster.tag}</span>
                  <span className="text-gray-500 text-[10px] ml-auto">Score: {cluster.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AgenticFactoryMockup = () => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
    {/* Title bar */}
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/80" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <div className="w-3 h-3 rounded-full bg-green-500/80" />
      </div>
      <span className="text-xs text-gray-400 ml-2 font-mono">Agentic Factory — Agent Builder</span>
    </div>
    <div className="p-5">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-dark-slate rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
          </div>
          <div>
            <h4 className="text-dark-slate font-bold text-sm">Agentic Factory</h4>
            <p className="text-gray-400 text-xs">3 agents deployed · 1 in review</p>
          </div>
        </div>
        <div className="px-3 py-1.5 bg-dark-slate text-white text-xs font-medium rounded-lg">+ New Agent</div>
      </div>
      {/* Agent pipeline */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {[
          { name: 'Support Triage Agent', status: 'Active', statusColor: 'bg-green-100 text-green-700', model: 'Claude 3.5', calls: '1,247/day', cost: '$12.40/day', icon: '🎧' },
          { name: 'Invoice Processor', status: 'Active', statusColor: 'bg-green-100 text-green-700', model: 'GPT-4o-mini', calls: '340/day', cost: '$3.20/day', icon: '📄' },
          { name: 'Lead Qualifier', status: 'Review', statusColor: 'bg-yellow-100 text-yellow-700', model: 'Claude Haiku', calls: '—', cost: 'Est. $8/day', icon: '🎯' },
        ].map((agent) => (
          <div key={agent.name} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <span className="text-2xl">{agent.icon}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${agent.statusColor}`}>{agent.status}</span>
            </div>
            <h5 className="text-dark-slate font-semibold text-sm mb-1">{agent.name}</h5>
            <div className="space-y-1 text-xs text-gray-500">
              <div className="flex justify-between"><span>Model</span><span className="text-dark-slate font-medium">{agent.model}</span></div>
              <div className="flex justify-between"><span>Volume</span><span className="text-dark-slate font-medium">{agent.calls}</span></div>
              <div className="flex justify-between"><span>Cost</span><span className="text-dark-slate font-medium">{agent.cost}</span></div>
            </div>
          </div>
        ))}
      </div>
      {/* Governance bar */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-dark-slate">Governance & Cost Controls</span>
          <span className="text-[10px] text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">All Checks Passing</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Monthly Budget', value: '$450/$1,000', pct: 45 },
            { label: 'PII Detection', value: 'Enabled', pct: 100 },
            { label: 'Approval Gates', value: '2 of 3 active', pct: 66 },
            { label: 'Audit Logging', value: '100% coverage', pct: 100 },
          ].map((item) => (
            <div key={item.label}>
              <div className="text-[10px] text-gray-500 mb-1">{item.label}</div>
              <div className="text-xs font-medium text-dark-slate mb-1">{item.value}</div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div className={`h-1 rounded-full ${item.pct === 100 ? 'bg-green-500' : item.pct > 60 ? 'bg-blue-500' : 'bg-yellow-500'}`} style={{ width: `${item.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const AItoRevenueMockup = () => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
    {/* Title bar */}
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/80" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <div className="w-3 h-3 rounded-full bg-green-500/80" />
      </div>
      <span className="text-xs text-gray-400 ml-2 font-mono">AI to Revenue — Opportunity Dashboard</span>
    </div>
    <div className="p-5">
      {/* Impact summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Annual Cost Savings', value: '$340K', trend: '+12% vs target', color: 'text-green-600' },
          { label: 'New Revenue Identified', value: '$1.2M', trend: '3 streams', color: 'text-blue-600' },
          { label: 'Processes Automated', value: '14', trend: 'of 22 targeted', color: 'text-purple-600' },
          { label: 'Time to Value', value: '6 wks', trend: 'avg implementation', color: 'text-orange-600' },
        ].map((stat) => (
          <div key={stat.label} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">{stat.label}</div>
            <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-[10px] text-gray-400">{stat.trend}</div>
          </div>
        ))}
      </div>
      {/* Opportunity pipeline */}
      <div className="mb-4">
        <h5 className="text-sm font-semibold text-dark-slate mb-3">AI Opportunity Pipeline</h5>
        <div className="space-y-2">
          {[
            { name: 'Customer Support Automation', impact: 'High', savings: '$120K/yr', status: 'Deployed', statusColor: 'bg-green-100 text-green-700', bar: 100 },
            { name: 'Invoice Processing with LLM', impact: 'High', savings: '$85K/yr', status: 'Deployed', statusColor: 'bg-green-100 text-green-700', bar: 100 },
            { name: 'Sales Lead Scoring Engine', impact: 'Medium', savings: '$60K/yr', status: 'Building', statusColor: 'bg-blue-100 text-blue-700', bar: 65 },
            { name: 'Content Generation Pipeline', impact: 'Medium', savings: '$45K/yr', status: 'Scoped', statusColor: 'bg-yellow-100 text-yellow-700', bar: 30 },
            { name: 'Predictive Churn Detection', impact: 'High', savings: '$180K rev', status: 'Discovery', statusColor: 'bg-gray-100 text-gray-600', bar: 10 },
          ].map((opp) => (
            <div key={opp.name} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-dark-slate truncate">{opp.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${opp.statusColor}`}>{opp.status}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full bg-dark-slate transition-all" style={{ width: `${opp.bar}%` }} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-semibold text-dark-slate">{opp.savings}</div>
                <div className="text-[10px] text-gray-400">{opp.impact} impact</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Engagement phases */}
      <div className="flex gap-2">
        {['Discovery', 'Scoping', 'Build', 'Deploy', 'Measure'].map((phase, i) => (
          <div key={phase} className="flex-1 text-center">
            <div className={`text-[10px] font-medium py-1.5 rounded-lg ${i < 3 ? 'bg-dark-slate text-white' : 'bg-gray-100 text-gray-500'}`}>{phase}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ClaudeCodeMockup = () => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
    {/* Title bar */}
    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-500/80" />
        <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
        <div className="w-3 h-3 rounded-full bg-green-500/80" />
      </div>
      <span className="text-xs text-gray-400 ml-2 font-mono">Enterprise Claude Code — Adoption Dashboard</span>
    </div>
    <div className="p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#D97757] rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          </div>
          <div>
            <h4 className="text-dark-slate font-bold text-sm">Claude Code Enterprise</h4>
            <p className="text-gray-400 text-xs">42 developers onboarded · 6 teams active</p>
          </div>
        </div>
        <div className="px-3 py-1.5 bg-[#D97757] text-white text-xs font-medium rounded-lg">View Reports</div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Developer Adoption', value: '87%', sub: '42 of 48 engineers', color: 'text-green-600' },
          { label: 'Avg Time Saved', value: '2.4h', sub: 'per developer per day', color: 'text-blue-600' },
          { label: 'Code Review Cycle', value: '-62%', sub: 'cycle time reduction', color: 'text-purple-600' },
          { label: 'Monthly Cost', value: '$6.2K', sub: '$148/dev · 3.1x ROI', color: 'text-orange-600' },
        ].map((stat) => (
          <div key={stat.label} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mb-1">{stat.label}</div>
            <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-[10px] text-gray-400">{stat.sub}</div>
          </div>
        ))}
      </div>
      {/* Rollout phases */}
      <div className="mb-4">
        <h5 className="text-sm font-semibold text-dark-slate mb-3">Rollout Progress by Team</h5>
        <div className="space-y-2">
          {[
            { team: 'Platform Engineering', devs: '12/12', status: 'Complete', statusColor: 'bg-green-100 text-green-700', bar: 100, hooks: 3, policies: 'Full' },
            { team: 'Backend Services', devs: '10/10', status: 'Complete', statusColor: 'bg-green-100 text-green-700', bar: 100, hooks: 5, policies: 'Full' },
            { team: 'Frontend', devs: '8/8', status: 'Complete', statusColor: 'bg-green-100 text-green-700', bar: 100, hooks: 2, policies: 'Full' },
            { team: 'Data Engineering', devs: '6/8', status: 'In Progress', statusColor: 'bg-blue-100 text-blue-700', bar: 75, hooks: 1, policies: 'Partial' },
            { team: 'Mobile', devs: '4/6', status: 'In Progress', statusColor: 'bg-blue-100 text-blue-700', bar: 66, hooks: 0, policies: 'Pending' },
            { team: 'QA & Automation', devs: '2/4', status: 'Pilot', statusColor: 'bg-yellow-100 text-yellow-700', bar: 50, hooks: 0, policies: 'Pending' },
          ].map((t) => (
            <div key={t.team} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-dark-slate">{t.team}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${t.statusColor}`}>{t.status}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className="h-1.5 rounded-full bg-[#D97757] transition-all" style={{ width: `${t.bar}%` }} />
                </div>
              </div>
              <div className="hidden md:flex gap-4 text-right shrink-0">
                <div>
                  <div className="text-xs font-semibold text-dark-slate">{t.devs}</div>
                  <div className="text-[10px] text-gray-400">Devs</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-dark-slate">{t.hooks}</div>
                  <div className="text-[10px] text-gray-400">Hooks</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-dark-slate">{t.policies}</div>
                  <div className="text-[10px] text-gray-400">Policies</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Governance footer */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-dark-slate">Enterprise Controls</span>
          <span className="text-[10px] text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">SOC 2 Compliant</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Permission Policies', value: '6 active', pct: 100 },
            { label: 'Pre-commit Hooks', value: '11 deployed', pct: 85 },
            { label: 'Secrets Detection', value: 'Enabled', pct: 100 },
            { label: 'Audit Logging', value: '100% coverage', pct: 100 },
          ].map((item) => (
            <div key={item.label}>
              <div className="text-[10px] text-gray-500 mb-1">{item.label}</div>
              <div className="text-xs font-medium text-dark-slate mb-1">{item.value}</div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div className={`h-1 rounded-full ${item.pct === 100 ? 'bg-green-500' : 'bg-[#D97757]'}`} style={{ width: `${item.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   SOLUTION DATA
   ───────────────────────────────────────────────────────────────── */

const solutions = [
  {
    id: 'sentiment-engine',
    title: 'Sentiment Analysis & Automatic Ad Engine',
    subtitle: 'North Marketing Engine (NME)',
    tags: ['Signal Intelligence', 'Content Generation', 'Ad Automation', 'LLM Pipeline', 'LinkedIn Ads'],
    paragraphs: [
      'The North Marketing Engine is a production-grade, AI-powered content and advertising pipeline that transforms raw industry signals into targeted marketing content — automatically. It continuously monitors sources like Hacker News, news APIs, and podcasts, aggregates them into signal reports, and uses LLMs to identify topic clusters with relevance scores. From there, it generates both organic LinkedIn posts and paid ad copy, complete with headlines, pain points, CTAs, and alternative headlines — all tailored to your service categories and brand voice.',
      'This is not a content suggestion tool. NME runs a full pipeline: ingest signals, analyze sentiment and trending topics via Claude or GPT, generate draft content mapped to your service lines, and queue it for human review. The review interface lets you approve, reject, or edit each piece before publishing. It tracks content status (draft, approved, published, rejected), supports configurable posting cadence (posts per week, ads per week), and gives you a real-time dashboard showing signal report history, content breakdown, and pipeline configuration.',
      'The engine was built to solve a specific problem: most B2B companies know they should be creating timely, relevant content tied to industry trends, but they lack the time and operational process to do it consistently. NME closes that gap with a system that runs on autopilot with a human-in-the-loop approval layer. It is currently deployed internally at North and available for licensing or white-label deployment for clients who want to operationalize their thought leadership and ad spend.',
    ],
    deliverables: [
      'Automated signal ingestion from HN, news APIs, podcasts, and RSS feeds',
      'LLM-powered sentiment analysis and topic clustering with relevance scoring',
      'AI-generated organic posts and ad copy mapped to your service categories',
      'Human-in-the-loop review queue with approve, reject, and edit workflows',
      'Configurable posting cadence, ad budget controls, and brand voice settings',
      'Real-time dashboard with signal reports, content pipeline, and performance metrics',
    ],
    Mockup: NMEMockup,
  },
  {
    id: 'agentic-factory',
    title: 'Agentic Factory',
    subtitle: 'Build, Deploy & Govern AI Agents at Scale',
    tags: ['AI Agents', 'Enterprise Governance', 'Cost Controls', 'Secure Deployment', 'Multi-Model'],
    paragraphs: [
      'Every enterprise is going to need AI agents. The question is whether they build them ad hoc — scattered across teams with no governance, no cost controls, and no security review — or whether they build them properly from the start. Agentic Factory is a consulting-led engagement that helps your organization stand up a repeatable framework for building, deploying, and governing AI agents internally. We work with your engineering and leadership teams to design the architecture, set up the guardrails, and ship your first agents into production.',
      'The framework covers the full agent lifecycle: ideation and use-case prioritization, model selection (Claude, GPT, Gemini, open-source), prompt engineering and tool integration, testing and evaluation, deployment with cost budgets and approval gates, and ongoing monitoring with audit logging. We are model-agnostic and will recommend the right LLM for each use case based on capability, latency, cost, and your data residency requirements. The goal is to give your teams the ability to go from idea to deployed agent in days, not months.',
      'Governance is built into the framework from day one — not bolted on after the fact. That means PII detection, human-in-the-loop approval workflows for high-stakes actions, spend caps per agent, per team, and per month, and complete audit trails of every agent interaction. We have seen companies waste hundreds of thousands of dollars on runaway token costs or ship agents that leak customer data because they skipped the governance layer. Agentic Factory prevents both by baking cost controls and security into the deployment pipeline itself.',
    ],
    deliverables: [
      'Agent development framework with templates, tooling, and CI/CD pipelines',
      'Multi-model architecture supporting Claude, GPT, Gemini, and open-source LLMs',
      'Governance layer with PII detection, approval gates, and audit logging',
      'Cost management dashboard with per-agent, per-team, and per-month budgets',
      'Agent evaluation suite with automated testing, regression checks, and benchmarks',
      'Knowledge transfer and training for your engineering teams to self-serve',
    ],
    Mockup: AgenticFactoryMockup,
  },
  {
    id: 'ai-to-revenue',
    title: 'AI to Revenue',
    subtitle: 'Turn AI Investment into Measurable Business Impact',
    tags: ['AI Strategy', 'Cost Reduction', 'Revenue Growth', 'Process Automation', 'Consulting'],
    paragraphs: [
      'Most companies are spending money on AI without a clear line to business impact. They have a few proof-of-concepts, some prompt engineering experiments, and a growing API bill — but no structured approach to identifying which AI investments will actually move the needle. AI to Revenue is a consulting engagement designed to fix that. We work with your leadership team to systematically audit your operations, identify the highest-ROI automation and AI opportunities, and build a prioritized roadmap that ties every initiative to either cost reduction or revenue growth.',
      'The engagement starts with a two-week discovery phase where we map your business processes, interview stakeholders across departments, and analyze your operational data to find the leverage points. We are looking for three things: processes that can be automated to reduce headcount costs or cycle times, existing workflows where AI can improve quality or throughput, and underleveraged or entirely new revenue streams that become viable with AI capabilities. Every opportunity gets scored on impact, feasibility, and time-to-value, so your leadership team can make informed investment decisions.',
      'This is not a strategy deck that sits on a shelf. After the discovery phase, we move into implementation — building and deploying the top-priority initiatives with your team. Each implementation follows a six-week sprint model: scope, build, deploy, measure. We track hard metrics — dollars saved, revenue generated, hours recovered — and report them back to your executive team monthly. The goal is to make your AI investment self-funding within the first quarter by deploying high-impact automations that pay for the engagement and then some.',
    ],
    deliverables: [
      'Comprehensive AI opportunity audit across all business functions',
      'Prioritized roadmap with ROI projections, feasibility scores, and timelines',
      'Implementation sprints for top-priority cost reduction and revenue initiatives',
      'Monthly executive reporting with hard metrics: cost saved, revenue generated, hours recovered',
      'Change management support to drive adoption across teams',
      'Ongoing optimization and expansion of AI capabilities post-engagement',
    ],
    Mockup: AItoRevenueMockup,
  },
  {
    id: 'claude-code-enterprise',
    title: 'Enterprise Claude Code Adoption',
    subtitle: 'Roll Out AI-Assisted Development Across Your Engineering Org',
    tags: ['Claude Code', 'Developer Productivity', 'Enterprise Rollout', 'AI Coding', 'Governance'],
    paragraphs: [
      'Claude Code is the most capable AI coding assistant available today — but deploying it across an engineering organization of 20, 50, or 200 developers is not as simple as handing out licenses. Enterprise adoption requires a structured rollout that addresses security policies, permission configurations, workflow integration, custom hooks, team-specific prompt libraries, and cost governance. That is what this engagement delivers. We take your engineering org from zero to full Claude Code adoption with a phased rollout designed to maximize developer productivity while maintaining your security and compliance requirements.',
      'The engagement starts with a pilot phase: we select a high-impact team, configure Claude Code with your codebase context, set up permission policies that align with your security posture, and build custom pre-commit hooks that enforce your coding standards, prevent secrets leakage, and ensure AI-generated code passes your quality gates before it ever hits a pull request. We measure developer velocity, code review cycle time, and output quality during the pilot to build the business case for broader rollout. Most teams see a 40-60% reduction in code review cycle time and 2-3 hours saved per developer per day within the first two weeks.',
      'From the pilot, we expand team by team with a repeatable playbook. Each team gets a tailored onboarding that includes Claude Code configuration for their specific tech stack and repositories, custom slash commands and prompt templates for their common workflows, integration with their CI/CD pipeline and code review process, and hands-on training sessions. The governance layer runs across the entire org: centralized cost dashboards showing spend per team and per developer, audit logs of all AI interactions for compliance, and policy controls that let you restrict which repositories, file types, or operations Claude Code can access. You get all the productivity gains of AI-assisted development without the wild west.',
    ],
    deliverables: [
      'Phased rollout plan with pilot team selection, success metrics, and expansion criteria',
      'Enterprise permission policies and security configuration aligned to your compliance requirements',
      'Custom pre-commit hooks for secrets detection, code standards, and quality gates',
      'Team-specific prompt libraries, slash commands, and workflow integrations',
      'Centralized cost governance dashboard with per-team and per-developer spend tracking',
      'Hands-on training sessions and adoption playbook for self-service team onboarding',
    ],
    Mockup: ClaudeCodeMockup,
  },
];

/* ─────────────────────────────────────────────────────────────────
   PAGE COMPONENT
   ───────────────────────────────────────────────────────────────── */

const SolutionsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="AI Solutions — Sentiment Engine, Agentic Factory, AI to Revenue & Enterprise Claude Code"
        description="Production-ready AI solutions from North: the Sentiment Analysis & Ad Engine for automated content marketing, Agentic Factory for enterprise AI agent deployment, AI to Revenue consulting for measurable business impact, and Enterprise Claude Code Adoption for engineering orgs."
        path="/solutions"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.northinteractive.co/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Solutions',
                item: 'https://www.northinteractive.co/solutions',
              },
            ],
          })}
        </script>
      </Helmet>

      {/* ──────────────────────────── HERO ──────────────────────────── */}
      <section className="bg-gradient-to-br from-pink-50 via-white to-cream pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-8 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                Solutions
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-dark-slate mb-6 tracking-tight leading-tight"
            >
              AI Products &
              <br />
              <span className="text-muted-blue-grey">Consulting Solutions</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl md:text-2xl text-muted-grey max-w-3xl mx-auto mb-10 leading-relaxed"
            >
              Production-grade AI products and structured consulting engagements
              that deliver measurable business outcomes — not slide decks.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-dark-slate text-white rounded-full font-semibold text-lg hover:bg-deep-charcoal transition-all shadow-lg hover:shadow-xl inline-block"
              >
                Book a Strategy Call
              </motion.a>
              <Link
                to="/services"
                className="px-8 py-4 bg-white text-dark-slate rounded-full font-semibold text-lg border-2 border-gray-200 hover:border-gray-300 transition-all inline-block"
              >
                View Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ──────── QUICK NAV ──────── */}
      <section className="py-12 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <motion.div
            {...fadeIn}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {solutions.map((sol, idx) => (
              <motion.a
                key={sol.id}
                href={`#${sol.id}`}
                {...stagger}
                transition={{ duration: 0.5, delay: 0.1 * idx }}
                className="group p-6 bg-cream rounded-2xl border border-gray-100 hover:shadow-lg transition-all cursor-pointer block"
                whileHover={{ y: -4 }}
              >
                <div className="text-3xl font-bold text-gray-200 mb-2">0{idx + 1}</div>
                <h3 className="text-lg font-bold text-dark-slate mb-2 group-hover:text-muted-blue-grey transition-colors">
                  {sol.title}
                </h3>
                <p className="text-sm text-muted-grey">{sol.subtitle}</p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ──────────────────── SOLUTION SECTIONS ──────────────────── */}
      {solutions.map((solution, index) => {
        const isEven = index % 2 === 0;
        const MockupComponent = solution.Mockup;

        return (
          <section
            key={solution.id}
            id={solution.id}
            className={`py-24 px-6 ${isEven ? 'bg-white' : 'bg-cream'}`}
          >
            <div className="max-w-6xl mx-auto">
              {/* Tags */}
              <motion.div
                {...stagger}
                transition={{ duration: 0.5 }}
                className="flex flex-wrap gap-2 mb-6"
              >
                {solution.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 text-muted-grey text-sm font-medium rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </motion.div>

              {/* Title & Subtitle */}
              <motion.h2
                {...fadeIn}
                className="text-4xl md:text-5xl font-bold text-dark-slate mb-2 tracking-tight leading-tight"
              >
                {solution.title}
              </motion.h2>
              <motion.p
                {...stagger}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-xl text-muted-blue-grey font-medium mb-8"
              >
                {solution.subtitle}
              </motion.p>

              {/* Mockup */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-12"
              >
                <MockupComponent />
              </motion.div>

              {/* Paragraphs */}
              <div className="max-w-5xl space-y-6 mb-12">
                {solution.paragraphs.map((para, pIdx) => (
                  <motion.p
                    key={pIdx}
                    {...stagger}
                    transition={{ duration: 0.5, delay: 0.1 * (pIdx + 1) }}
                    className="text-lg text-muted-grey leading-relaxed"
                  >
                    {para}
                  </motion.p>
                ))}
              </div>

              {/* What We Deliver */}
              <motion.div
                {...fadeIn}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="max-w-5xl bg-gradient-to-br from-dark-navy to-deep-charcoal rounded-3xl p-10 md:p-12 text-white relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-40 h-40 bg-warm-tan opacity-15 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-muted-blue-grey opacity-15 rounded-full blur-3xl" />

                <div className="relative z-10">
                  <h3 className="text-2xl font-bold mb-6">What You Get</h3>
                  <ul className="grid md:grid-cols-2 gap-4">
                    {solution.deliverables.map((item, dIdx) => (
                      <motion.li
                        key={dIdx}
                        {...stagger}
                        transition={{ duration: 0.4, delay: 0.05 * dIdx }}
                        className="flex items-start gap-3"
                      >
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-warm-tan shrink-0" />
                        <span className="text-gray-300 leading-relaxed">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* ──────────────────── WHY SOLUTIONS ──────────────────── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeIn} className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 bg-cream rounded-full mb-6 border border-gray-100"
            >
              <span className="text-sm font-medium text-muted-grey">
                Why Solutions
              </span>
            </motion.div>

            <h2 className="text-4xl md:text-5xl font-bold text-dark-slate mb-6 tracking-tight">
              Built Different
            </h2>

            <p className="text-xl text-muted-grey max-w-3xl mx-auto leading-relaxed">
              These are not concepts or pitch decks. Every solution listed here is either
              running in production today or has been deployed for clients. We build first,
              sell second.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Production-Grade',
                description:
                  'Every solution is built to run in production with proper error handling, monitoring, cost controls, and security. We do not ship prototypes. If it is on this page, it works at scale.',
              },
              {
                step: '02',
                title: 'Measurable ROI',
                description:
                  'Every engagement is structured around hard metrics — dollars saved, revenue generated, time recovered. We track outcomes monthly and report to your executive team. No ambiguity about whether AI is delivering value.',
              },
              {
                step: '03',
                title: 'Your Team Owns It',
                description:
                  'We build with you, not for you. Knowledge transfer is built into every engagement. Your team learns the architecture, the tooling, and the operational playbook. When we leave, you keep running.',
              },
            ].map((phase, idx) => (
              <motion.div
                key={phase.step}
                {...stagger}
                transition={{ duration: 0.5, delay: 0.15 * idx }}
                className="bg-cream rounded-3xl p-8 border border-gray-100"
              >
                <div className="text-5xl font-bold text-gray-200 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold text-dark-slate mb-3">{phase.title}</h3>
                <p className="text-muted-grey leading-relaxed">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────── CTA ──────────────────────── */}
      <section className="py-24 px-6 bg-gradient-to-br from-dark-navy to-deep-charcoal relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-warm-tan opacity-10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-muted-blue-grey opacity-10 rounded-full blur-3xl" />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to See What AI Can Actually Do?
            </h2>

            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Book a 30-minute call and we will walk through which solution fits your
              business, what the implementation looks like, and what kind of ROI you
              can realistically expect.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href="https://calendar.app.google/ube4SfQSbn6SqRCCA"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-dark-navy rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl inline-block"
              >
                Book a Strategy Call
              </motion.a>
              <a
                href="mailto:info@northinteractive.co"
                className="px-8 py-4 text-white border-2 border-white/30 rounded-full font-semibold text-lg hover:border-white/60 transition-all inline-block"
              >
                info@northinteractive.co
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
